/**
 * MemberProfilePrintView — single-member printable profile, opened from
 * MemberProfileModal's header Print button.
 *
 * Landscape letter page, single record (no pagination). Field values are
 * re-derived directly from the member record via a local field() helper —
 * this mirrors MemberProfileModal's on-screen field-to-source mapping in
 * comments only, not in code (this codebase already keeps screen/print
 * rendering separate per-column in team-management-columns.tsx, via
 * renderCell/renderPrintCell). No colored badges anywhere — every field
 * prints as plain label/value text, matching the prototype exactly.
 *
 * CSS lives under the `emp-profile-*` namespace, isolated from the roster
 * print's `team-report-*` (team-management-print-view.tsx).
 */
import { useTranslation } from 'react-i18next';
import { Box, HStack, Modal } from 'native-base';
import { Button, Text } from '@pimm/base';
import type { ColumnContext } from './team-management-columns';
import type { TeamMember } from '../types';
import { RECOVERY_EMAIL_STATUS_VERIFIED } from '../types';
import { formatBirthday, formatDateShort } from './team-management-table-shared';
import { REPORT_CHROME_STYLES, ReportFooter, ReportHeader, ReportSectionBar, buildExportFileName, printWithFileName } from './team-management-report-chrome';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  member: TeamMember;
  columnContext: ColumnContext;
  siteName: string;
  customerName: string;
  address: string;
  chainName: string;
  vendorName?: string;
  brandLogoUrl?: string;
  generatedByName: string;
};

const PRINT_STYLES = `
  .emp-profile-screen-wrap {
    background: #f3f4f6;
    padding: 24px 0;
    overflow-y: auto;
    max-height: calc(90vh - 56px);
  }
  .emp-profile-paper {
    width: 11in;
    min-height: 8.5in;
    margin: 0 auto;
    background: white;
    box-shadow: 0 2px 12px rgba(15, 23, 42, 0.12);
    padding: 0.5in 0.5in;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    font-family: 'Inter', sans-serif;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .emp-profile-section { margin-top: 16px; }
  .emp-profile-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px 24px;
    /* Matches .rpt-section-bar's 10px left padding — column 1 values sit
       flush with the section title's left edge instead of starting further
       left than it. */
    padding: 0 10px;
  }
  .emp-profile-grid + .emp-profile-grid { margin-top: 12px; }
  .emp-profile-field { break-inside: avoid; }
  .emp-profile-label {
    font-size: 9.5px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 2px;
  }
  .emp-profile-value { font-size: 12.5px; color: #111827; }
  .emp-profile-value.muted { color: #9ca3af; }
  .emp-profile-callout {
    font-size: 12px;
    color: #6b7280;
    font-style: italic;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 10px 12px;
  }

  @media print {
    @page { size: letter landscape; margin: 0; }
    html, body {
      background: white !important;
      margin: 0 !important;
      height: auto !important;
      overflow: visible !important;
    }
    body > *:not(.emp-profile-print-modal) { display: none !important; }
    .emp-profile-print-modal {
      display: block !important;
      position: static !important;
      width: 100% !important;
      height: auto !important;
      max-height: none !important;
      overflow: visible !important;
      background: white !important;
      box-shadow: none !important;
      border-radius: 0 !important;
      padding: 0 !important;
      margin: 0 !important;
    }
    .emp-profile-print-chrome { display: none !important; }
    .emp-profile-screen-wrap {
      padding: 0 !important;
      background: white !important;
      overflow: visible !important;
      max-height: none !important;
    }
    .emp-profile-paper { box-shadow: none !important; margin: 0 !important; min-height: auto !important; }
  }
`;

const field = (label: string, value: string | null | undefined) => (
  <div className="emp-profile-field">
    <div className="emp-profile-label">{label}</div>
    <div className={`emp-profile-value${value ? '' : ' muted'}`}>{value || '—'}</div>
  </div>
);

export const MemberProfilePrintView = ({
  isOpen,
  onClose,
  member: m,
  columnContext,
  siteName,
  customerName,
  address,
  chainName,
  vendorName,
  brandLogoUrl,
  generatedByName,
}: Props) => {
  const { t } = useTranslation();

  // Reparent the print-modal div to <body> before window.print() — defeats
  // native-base Modal's viewport clipping (same fix team-management-print-view.tsx
  // already needed). Restore on afterprint.
  const handlePrint = () => {
    const fileName = buildExportFileName(siteName, t('store_settings:employee_profile'), new Date(), `${m.firstName} ${m.lastName}`);
    const node = document.querySelector('.emp-profile-print-modal');
    if (!node || !node.parentNode) {
      printWithFileName(fileName, () => window.print());
      return;
    }
    const placeholder = document.createComment('print-modal-placeholder');
    node.parentNode.insertBefore(placeholder, node);
    document.body.appendChild(node);
    const restore = () => {
      window.removeEventListener('afterprint', restore);
      if (placeholder.parentNode) {
        placeholder.parentNode.insertBefore(node, placeholder);
        placeholder.parentNode.removeChild(placeholder);
      }
    };
    window.addEventListener('afterprint', restore);
    printWithFileName(fileName, () => window.print());
  };

  const isPrimary = !!m.user?.isPrimary;
  const subtitle = `${m.firstName} ${m.lastName} · ${chainName}${isPrimary ? ` · ★ ${t('store_settings:primary_manager')}` : ''}`;
  const now = new Date();
  // Header-right shows just the date (no time) — matches the roster PrintView's reportDateText.
  const reportDateText = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const dateTimeText = now.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  const footerCenterText = generatedByName
    ? `${t('store_settings:report_generated_at')} ${dateTimeText} · ${generatedByName}`
    : `${t('store_settings:report_generated_at')} ${dateTimeText}`;

  const importSourceLabel =
    m.importSource === 'Manual' ? t('store_settings:import_source_manual') : t('store_settings:import_source_importer');
  const autoSyncLabel =
    m.importSyncMode === 'Locked'
      ? columnContext.autoSyncOffLabel
      : m.importSyncMode === 'Protected'
        ? columnContext.autoSyncProtectedLabel
        : columnContext.autoSyncOnLabel;
  const recoveryEmail =
    m.user?.recoveryEmail && m.user.recoveryEmailStatus === RECOVERY_EMAIL_STATUS_VERIFIED ? m.user.recoveryEmail : undefined;

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: PRINT_STYLES + REPORT_CHROME_STYLES }} />
      <Modal isOpen={isOpen} onClose={onClose} size="full">
        <Modal.Content maxW="95%" maxH="95%">
          <div className="emp-profile-print-modal">
            {/* native-base's Box props don't declare `className`, but native-base-web forwards
                unknown props to the underlying DOM node — needed here so the `@media print`
                selector in PRINT_STYLES can hide this on-screen-only chrome bar. */}
            <Box
              {...({ className: 'emp-profile-print-chrome' } as any)}
              px={4}
              py={3}
              bg="white"
              borderBottomWidth={1}
              borderColor="gray.200"
            >
              <HStack alignItems="center" justifyContent="space-between">
                <Text size="md" fontWeight={600} color="gray.900">
                  {t('store_settings:employee_profile')} · {`${m.firstName} ${m.lastName}`}
                </Text>
                <HStack space={2}>
                  <Button alternate outline size="sm" onPress={onClose}>
                    {t('store_settings:close')}
                  </Button>
                  <Button size="sm" onPress={handlePrint}>
                    {t('store_settings:print_button_label')}
                  </Button>
                </HStack>
              </HStack>
            </Box>
            <div className="emp-profile-screen-wrap">
              <div className="emp-profile-paper">
                <ReportHeader logoUrl={brandLogoUrl} title={t('store_settings:employee_profile')} subtitle={subtitle} rightLines={[reportDateText]} />

                <div className="emp-profile-section">
                  <ReportSectionBar>{t('store_settings:report_store_information')}</ReportSectionBar>
                  <div className="emp-profile-grid">
                    {field(t('store_settings:report_customer'), customerName)}
                    {field(t('store_settings:report_store'), siteName)}
                    {field(t('store_settings:address'), address)}
                  </div>
                </div>

                <div className="emp-profile-section">
                  <ReportSectionBar>{t('store_settings:section_boh_integration')}</ReportSectionBar>
                  <div className="emp-profile-grid">
                    {field(t('store_settings:import_source'), importSourceLabel)}
                    {field(t('store_settings:back_office_vendor'), vendorName)}
                    {field(t('store_settings:employee_id'), m.employeeNum)}
                  </div>
                  <div className="emp-profile-grid">
                    {field(columnContext.autoSyncPrefixLabel, autoSyncLabel)}
                    {field(t('store_settings:employment_status'), columnContext.statusLabels[m.status])}
                    {field(t('store_settings:role'), columnContext.roleLabels[m.role])}
                  </div>
                </div>

                <div className="emp-profile-section">
                  <ReportSectionBar>{t('store_settings:section_employee_record')}</ReportSectionBar>
                  <div className="emp-profile-grid">
                    {field(t('store_settings:first_name'), m.firstName)}
                    {field(t('store_settings:last_name'), m.lastName)}
                    {field(t('store_settings:job_title'), m.title)}
                  </div>
                  <div className="emp-profile-grid">
                    {field(t('store_settings:birthday'), formatBirthday(m.birthDate))}
                    {field(t('store_settings:hire_date'), formatDateShort(m.hireDate))}
                    {field(t('store_settings:email'), m.email)}
                    {field(t('store_settings:mobile_phone'), m.mobileNumber)}
                  </div>
                </div>

                <div className="emp-profile-section">
                  <ReportSectionBar>{t('store_settings:section_user_information')}</ReportSectionBar>
                  {m.user ? (
                    <>
                      <div className="emp-profile-grid">
                        {field(t('store_settings:username'), m.user.userName)}
                        {field(t('store_settings:user_contact_email'), m.user.email)}
                        {field(t('store_settings:recovery_email'), recoveryEmail)}
                        {field(t('store_settings:pin_set'), m.user.pinSet ? columnContext.yesLabel : columnContext.noLabel)}
                      </div>
                      <div className="emp-profile-grid">
                        {field(t('store_settings:user_status'), columnContext.userStatusLabels[m.user.status])}
                        {field(t('store_settings:user_role'), columnContext.roleLabels[m.user.userRole])}
                      </div>
                    </>
                  ) : (
                    <div className="emp-profile-callout">{t('store_settings:no_login_callout')}</div>
                  )}
                </div>

                <div className="emp-profile-section">
                  <ReportSectionBar>{t('store_settings:section_history')}</ReportSectionBar>
                  <div className="emp-profile-grid">
                    {field(t('store_settings:last_login'), m.user ? formatDateShort(m.user.lastLoginDateUtc) : undefined)}
                    {field(t('store_settings:last_scheduled'), formatDateShort(m.lastScheduledDateUtc))}
                  </div>
                </div>

                <ReportFooter centerText={footerCenterText} pageLabel="" />
              </div>
            </div>
          </div>
        </Modal.Content>
      </Modal>
    </>
  );
};

export default MemberProfilePrintView;
